import { MutationLifecycleApi } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query/react';
import { RoleAssociatedUserDto } from 'types/employees';
import { PaginationProps } from 'types/paginationAndFilter';
import { SystemPrivilegeListItem } from 'types/systemPrivileges';
import { CreateSystemRoleDto, SystemRole, SystemRolePrivilegeUpdateDto } from 'types/systemRoles';
import { portalAPI } from './portalAPI';
import { APISystemPrivilegesSlice } from './systemPrivileges';
import { APIUserSlice } from './user';

const paginationQueryEndpoints = ['getSystemPrivilegesWithFilters'] as const;

const updateSystemRolePrivilegesInLists = async (
    systemPrivileges: SystemPrivilegeListItem[],
    api: MutationLifecycleApi<
        SystemRolePrivilegeUpdateDto,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
        SystemPrivilegeListItem[],
        'portalAPI'
    >
) => {
    const queries = api.getState().portalAPI.queries;

    paginationQueryEndpoints.forEach((endpointName) => {
        const endpointQueries = Object.entries(queries)
            .filter((entry) => entry[1]?.endpointName === endpointName)
            .map((entry) => entry[1]);

        endpointQueries.forEach((query) => {
            api.dispatch(
                APISystemPrivilegesSlice.util.updateQueryData(
                    endpointName,
                    query?.originalArgs as PaginationProps,
                    (draft) => {
                        systemPrivileges.forEach((systemPrivilege) => {
                            const item = draft.content.find((item) => item.id === systemPrivilege.id);
                            if (item !== undefined) {
                                // `id` is intentionally stripped from the assignment.
                                const { id, ...rest } = systemPrivilege;
                                Object.assign(item, rest);
                            }
                        });
                    }
                )
            );
        });
    });
};

const APISystemRolesSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['SystemRoles', 'SystemRoleAssociatedUsers'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getSystemRolesList: builder.query<SystemRole[], void>({
                query: () => '/v1/system-roles',
                providesTags: ['SystemRoles'],
                keepUnusedDataFor: 0,
            }),
            getSystemRoleAssociatedUsersList: builder.query<RoleAssociatedUserDto[], number>({
                query: (id) => ({
                    url: `/v1/system-roles/${id}/associated-users`,
                }),
                providesTags: ['SystemRoleAssociatedUsers'],
                keepUnusedDataFor: 0,
            }),
            addSystemRole: builder.mutation<SystemRole, CreateSystemRoleDto>({
                query: ({ ...body }) => ({
                    url: '/v1/system-roles',
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['SystemRoles', 'SystemRoleAssociatedUsers'],
            }),
            updateSystemRolePrivileges: builder.mutation<SystemPrivilegeListItem[], SystemRolePrivilegeUpdateDto>({
                query: ({ roleId, ...body }) => ({
                    url: `/v1/system-roles/${roleId}/update-privileges`,
                    method: 'PATCH',
                    body,
                }),
                async onQueryStarted(_arg, api) {
                    const { dispatch, queryFulfilled } = api;
                    const { data } = await queryFulfilled;
                    // update cache immediately to avoid "re-sync" flicker
                    await updateSystemRolePrivilegesInLists(data, api);
                    dispatch(APISystemPrivilegesSlice.util.invalidateTags(['SystemPrivileges']));
                    dispatch(APIUserSlice.util.invalidateTags(['CurrentUser']));
                },
            }),
            deleteSystemRole: builder.mutation<void, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/system-roles/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['SystemRoles', 'SystemRoleAssociatedUsers'],
            }),
            updateSystemRoleAdministrativeRole: builder.mutation<
                SystemRole,
                { id: number; administrativeRole: boolean }
            >({
                query: ({ id, administrativeRole }) => ({
                    url: `/v1/system-roles/${id}/administrative-role`,
                    method: 'PATCH',
                    body: administrativeRole,
                }),
                invalidatesTags: ['SystemRoles'],
            }),
            updateSystemRoleSystemicRole: builder.mutation<SystemRole, { id: number; systemicRole: boolean }>({
                query: ({ id, systemicRole }) => ({
                    url: `/v1/system-roles/${id}/systemic-role`,
                    method: 'PATCH',
                    body: systemicRole,
                }),
                invalidatesTags: ['SystemRoles'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetSystemRolesListQuery,
    useGetSystemRoleAssociatedUsersListQuery,
    useUpdateSystemRolePrivilegesMutation,
    useAddSystemRoleMutation,
    useDeleteSystemRoleMutation,
    useUpdateSystemRoleAdministrativeRoleMutation,
    useUpdateSystemRoleSystemicRoleMutation,
} = APISystemRolesSlice;
